import React, { useEffect, useRef } from 'react';
import { FaBold, FaHeading, FaImage, FaItalic, FaLink, FaQuoteRight } from 'react-icons/fa';
import { useI18n } from '../utilities/i18n';
import { looksLikeNewsMarkdown, markdownToHtml, sanitizeNewsHtml } from '../utilities/newsApi';

const runCommand = (command, value = null) => {
  document.execCommand(command, false, value);
};

const BLOCK_TAGS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'li']);

const closestBlock = (node, root) => {
  let current = node;
  if (current && current.nodeType === 3) current = current.parentElement;
  while (current && current !== root) {
    if (current.nodeType === 1 && BLOCK_TAGS.has(current.tagName.toLowerCase())) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const unwrapElement = (el) => {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
};

export default function NewsRichEditor({ value, onChange, disabled, placeholder }) {
  const { t } = useI18n();
  const editorRef = useRef(null);
  const lastHtml = useRef('');
  const fileRef = useRef(null);

  useEffect(() => {
    const html = sanitizeNewsHtml(value);
    if (editorRef.current && html !== lastHtml.current) {
      editorRef.current.innerHTML = html;
      lastHtml.current = html;
    }
  }, [value]);

  const emitChange = () => {
    if (!editorRef.current) return;
    const html = sanitizeNewsHtml(editorRef.current.innerHTML);
    lastHtml.current = html;
    if (onChange) onChange(html);
  };

  const focusEditor = () => {
    if (editorRef.current) editorRef.current.focus();
  };

  const apply = (command, arg) => {
    if (disabled) return;
    focusEditor();
    runCommand(command, arg);
    emitChange();
  };

  const applyBlock = (tag) => {
    if (disabled) return;
    focusEditor();
    const applied = document.execCommand('formatBlock', false, tag);
    if (!applied) document.execCommand('formatBlock', false, `<${tag}>`);
    emitChange();
  };

  /** Надёжнее formatBlock: всегда получаем <blockquote>, иначе на save уходит plain text. */
  const toggleQuote = () => {
    if (disabled) return;
    focusEditor();
    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      document.execCommand('formatBlock', false, 'blockquote')
        || document.execCommand('formatBlock', false, '<blockquote>');
      emitChange();
      return;
    }

    let anchor = selection.anchorNode;
    if (anchor && anchor.nodeType === 3) anchor = anchor.parentElement;
    if (!anchor || !editor.contains(anchor)) {
      emitChange();
      return;
    }

    const existing = typeof anchor.closest === 'function' ? anchor.closest('blockquote') : null;
    if (existing && editor.contains(existing)) {
      unwrapElement(existing);
      emitChange();
      return;
    }

    document.execCommand('formatBlock', false, 'blockquote')
      || document.execCommand('formatBlock', false, '<blockquote>');

    let check = window.getSelection()?.anchorNode;
    if (check && check.nodeType === 3) check = check.parentElement;
    const created = check && typeof check.closest === 'function' ? check.closest('blockquote') : null;
    if (created && editor.contains(created)) {
      emitChange();
      return;
    }

    // Fallback: вручную обернуть текущий блок
    const block = closestBlock(selection.anchorNode, editor) || editor;
    if (block === editor) {
      const quote = document.createElement('blockquote');
      while (editor.firstChild) quote.appendChild(editor.firstChild);
      if (!quote.childNodes.length) quote.appendChild(document.createElement('br'));
      editor.appendChild(quote);
    } else if (block.tagName.toLowerCase() !== 'blockquote') {
      const quote = document.createElement('blockquote');
      block.parentNode.insertBefore(quote, block);
      quote.appendChild(block);
    }
    emitChange();
  };

  const applyLink = () => {
    if (disabled) return;
    const href = window.prompt(t('newsEditorLinkPh'), 'https://');
    if (!href) return;
    apply('createLink', href.trim());
  };

  const insertImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      focusEditor();
      runCommand('insertImage', reader.result);
      emitChange();
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={`news-editor ${disabled ? 'is-disabled' : ''}`}>
      <div className="news-editor-toolbar">
        <button type="button" className="news-editor-btn" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => apply('bold')} title={t('newsEditorBold')}>
          <FaBold />
        </button>
        <button type="button" className="news-editor-btn" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => apply('italic')} title={t('newsEditorItalic')}>
          <FaItalic />
        </button>
        <button type="button" className="news-editor-btn" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => applyBlock('h2')} title={t('newsEditorHeading')}>
          <FaHeading />
        </button>
        <button type="button" className="news-editor-btn" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={toggleQuote} title={t('newsEditorQuote')}>
          <FaQuoteRight />
        </button>
        <button type="button" className="news-editor-btn" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={applyLink} title={t('newsEditorLink')}>
          <FaLink />
        </button>
        <button
          type="button"
          className="news-editor-btn"
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => fileRef.current && fileRef.current.click()}
          title={t('newsEditorImage')}
        >
          <FaImage />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="st-photo-input"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            insertImageFile(file);
          }}
        />
      </div>
      <div
        ref={editorRef}
        className="news-editor-area"
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={(event) => {
          if (disabled) return;
          event.preventDefault();
          const html = event.clipboardData.getData('text/html');
          const text = event.clipboardData.getData('text/plain');
          const payload = looksLikeNewsMarkdown(text)
            ? markdownToHtml(text)
            : sanitizeNewsHtml(html || String(text || '').replace(/\n/g, '<br>'));
          runCommand('insertHTML', payload);
          emitChange();
        }}
      />
    </div>
  );
}
